import { ArrowBack as IconArrowBack } from "@mui/icons-material";
import { IconButton, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link, Outlet, useLocation, useParams } from "react-router";

import { MapPanel, MapPanelHeader, MapPanelScrollableArea } from "~/modules/map/components/MapPanel";
import { useSessionStorageSearchParams } from "~/shared/hooks/useSessionStorageSearchParams";

import { useProjectReviewVisitV2FunctionList_GetData } from "./api/useProjectReviewVisitV2FunctionList_GetData";
import { ProjectReviewVisitV2FunctionListSkeleton } from "./components/ProjectReviewVisitV2FunctionListSkeleton";

export interface ProjectReviewVisitV2FunctionListRouteOutletContext {
    ecologicalFunctions: ReturnType<typeof useProjectReviewVisitV2FunctionList_GetData>["ecologicalFunctions"];
    observations: ReturnType<typeof useProjectReviewVisitV2FunctionList_GetData>["observations"];
    page: number;
    pageSize: number;
    onPageChange: (nextValue: number) => void;
}

export function ProjectReviewVisitV2FunctionListRoute() {
    const { t } = useTranslation("project");
    const { clientSlug, projectSlug, visitId } = useParams();
    const location = useLocation();

    if (!clientSlug || !projectSlug || !visitId) {
        throw new Error("Missing `clientSlug`, `projectSlug`, or `visitId` parameter");
    }

    const [searchParams, setSearchParams] = useSessionStorageSearchParams({ bucket: visitId });
    const page = Number(searchParams.get("page") ?? "0");
    const pageSize = 5;
    const layerId = searchParams.get("layerId") ?? undefined;

    const { ecologicalFunctions, observations, loading } = useProjectReviewVisitV2FunctionList_GetData(
        visitId,
        pageSize,
        page,
        layerId,
    );

    const handlePageChange = useCallback(
        (page: number) =>
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.set("page", page.toString());
                return next;
            }),
        [setSearchParams],
    );
    // If the current page is empty but there are results, we should navigate back one page. This will
    // happen because the search parameters are persisted but the list on this page will drain
    // as the user approves observations.
    useEffect(() => {
        if (observations?.data === undefined || observations.queryInfo === undefined) {
            return;
        }

        if (page === 0) {
            return;
        }

        if (observations.data.length < 1 && observations.queryInfo.paginationOption?.total > 0) {
            handlePageChange(Math.max(page - 1, 0));
        }
    }, [handlePageChange, observations, page]);

    const backHref = generatePath("/c/:clientSlug/p/:projectSlug/review-visit/:visitId/", {
        clientSlug,
        projectSlug,
        visitId,
    });

    const activeTab = location.pathname.includes("created") ? "created-functions" : "possible-functions";

    const possibleFunctionsLabel = t("projectReviewVisitV2.functionsRoute.possibleFunctionsTab", {
        value: observations.queryInfo?.paginationOption.total ?? 0,
    });
    const createdFunctionsLabel = t("projectReviewVisitV2.functionsRoute.createdFunctionsTab", {
        value: ecologicalFunctions.queryInfo?.paginationOption.total ?? 0,
    });

    if (loading) {
        return <ProjectReviewVisitV2FunctionListSkeleton backHref={backHref} />;
    }

    return (
        <MapPanel>
            <MapPanelHeader>
                <IconButton component={Link} color="inherit" to={backHref}>
                    <IconArrowBack />
                </IconButton>

                <Stack paddingBlock={1} flexGrow={1}>
                    <Typography variant="h6">{t("projectReviewVisitV2.functionsRoute.title")}</Typography>
                </Stack>
            </MapPanelHeader>

            <Tabs variant="fullWidth" value={activeTab}>
                <Tab value="possible-functions" component={Link} to="." label={possibleFunctionsLabel} />
                <Tab value="created-functions" component={Link} to="created" label={createdFunctionsLabel} />
            </Tabs>

            <MapPanelScrollableArea sx={{ padding: 0 }}>
                <Outlet context={{ ecologicalFunctions, observations, page, pageSize, onPageChange: handlePageChange }} />
            </MapPanelScrollableArea>
        </MapPanel>
    );
}
